import { MediaContentTypes, MediaTypes } from '@/types/enums'
import { MediaImageData } from './MediaImage'
import { MediaVideoData } from './MediaVideo'
import { MediaEmbedData } from './MediaVideoEmbed'
import { MediaDataTypes } from './MediaData'

export default function createUmbracoMediaItemProps({
  mediaItemContent,
  umbracoBaseUrl,
}: {
  mediaItemContent: any
  umbracoBaseUrl: string
}): MediaDataTypes | undefined {
  const contentType = mediaItemContent?.contentType

  // Fetch all possible enum values.
  const mediaTypeEntries = Object.entries(MediaContentTypes)

  // Check if the incoming content type matches a value of our enum.
  let mediaTypeKey = null
  for (const [key, value] of mediaTypeEntries) {
    if (contentType?.toLowerCase() === value?.toLowerCase()) {
      mediaTypeKey = key
      break
    }
  }

  // Fetch the correct enum key based on passed value.
  const mediaType = MediaTypes[mediaTypeKey as keyof typeof MediaTypes]
  // Conditionally render the MediaElement with the correct properties.
  const mediaItemProperties = mediaItemContent?.properties
  
  let mediaItem: MediaImageData | MediaVideoData | MediaEmbedData | undefined
  switch (mediaType) {
    case MediaTypes.Image:
      mediaItem = generateImageData(mediaItemProperties, umbracoBaseUrl)
      break
    case MediaTypes.Video:
      mediaItem = {
        type: MediaTypes.Video,
        videoSrc: `${umbracoBaseUrl}${(mediaItemProperties?.video?.[0].url).replace(/^\//, '')}`,
        showControls: mediaItemProperties?.showControls,
        isAutoPlay: mediaItemProperties?.autoPlay,
      }
      break
    case MediaTypes.Embed:
      mediaItem = {
        type: MediaTypes.Embed,
        embedCode: mediaItemProperties?.embedCode,
      }
      break
  }

  return mediaItem
}

export function generateImageData(
  content: any,
  umbracoBaseUrl: string,
): MediaImageData | undefined {
  const image = content?.image?.[0]

  if (!image) {
    return
  }

  const cropSizes = image.crops?.map((crop: any) => ({
    width: crop.width,
    height: crop.height,
    alias: crop.alias,
    coordinates: crop.coordinates
      ? {
          left: crop.coordinates.x1,
          top: crop.coordinates.y1,
          right: crop.coordinates.x2,
          bottom: crop.coordinates.y2,
        }
      : undefined,
  }))

  const focalPoint = image.focalPoint
    ? { x: image.focalPoint.left, y: image.focalPoint.top }
    : undefined

  const size = { width: image.width, height: image.height }

  return {
    type: MediaTypes.Image,
    imageSrc: `${umbracoBaseUrl}${image?.url?.replace(/^\//, '')}`,
    altText: content?.altText,
    cropSizes: cropSizes,
    focalPoint: focalPoint,
    originalSize: size,
    bytes: image.bytes,
  }
}
